import { PREINSTALLED_SOFTWARE } from '@amzn/awscat-portfolio-assessment-types';
import { createIntl, FormatNumberOptions, FormattedNumber } from 'react-intl';

import { constants } from './Localization';

const DEFAULT_PERCENTAGE_DIGITS = 2;

export function getLocale() {
    return (navigator.languages && navigator.languages[0])
        || navigator.language
        || 'en-US';
}

// Used for returning a formatted string
const intl = createIntl({
    locale: getLocale()
});

export function formatNumber(value: number | undefined | null, options?: FormatNumberOptions): JSX.Element | string {
    if (value == null) {
        return '';
    }
    const emptyOptions: FormatNumberOptions = {
        currency: undefined,
        currencyDisplay: undefined,
        maximumFractionDigits: undefined,
        minimumFractionDigits: undefined,
        style: undefined,
    };

    const { currency, currencyDisplay, minimumFractionDigits, maximumFractionDigits, style } = Object.assign(emptyOptions, options);
    return (
        <FormattedNumber
            currency={currency}
            currencyDisplay={currencyDisplay}
            style={style}
            value={value}
            minimumFractionDigits={minimumFractionDigits}
            maximumFractionDigits={maximumFractionDigits}
        />
    );
}

export function getCurrencySymbol(currency: string): string {
    return currency === 'CNY' ? '¥' : '$';
}

export function formatCurrency(value: number | undefined | null, options: FormatNumberOptions): JSX.Element | string {
    if (value == null) {
        return '';
    }
    options.style = 'currency';
    options.currencyDisplay = 'symbol';
    return formatNumber(value, options);
}

export const formatCurrencyNoDecimals = (value: number | undefined | null, currency: string) => {
    if (value === -0) { // can happen for RI volume discount
        value = 0;
    }
    return formatCurrency(value, {currency, maximumFractionDigits: 0, minimumFractionDigits: 0});
};

export function formatPercentage(value: number | undefined | null): JSX.Element | string {
    if (value == null) {
        return '';
    }
    const options: FormatNumberOptions = { maximumFractionDigits: DEFAULT_PERCENTAGE_DIGITS, style: 'percent' };
    return formatNumber(value, options);
}

/**
 * Convert a number to a string formatted as a percentage (multiplied by 100 and displayed with %)
 * @param value 
 * @returns 
 */
export function formatPercentageAsString(value: number | undefined | null): string {
    if (!value) {
        return '';
    }
    const options: FormatNumberOptions = { maximumFractionDigits: DEFAULT_PERCENTAGE_DIGITS, style: 'percent' };
    return formatNumberAsString(value, options);
}

export function formatNumberAsString(value: number | undefined | null, options?: FormatNumberOptions): string {
    if (value == null) {
        return '';
    }
    return intl.formatNumber(value, options);
}

export function formatCurrencyAsString(value: number | undefined | null, options: FormatNumberOptions): string {
    if (value == null) {
        return '';
    }
    options.style = 'currency';
    options.currencyDisplay = 'symbol';
    return formatNumberAsString(value, options);
}

/**
 * formats preinstalled software
 * @param {string} preInstalledSw server value
 * @returns {string} Returns the value preinstalled software.
 */
export function formatPreInstalledSw(preInstalledSw?: string): string {
    let formattedPreInstalledSw = '-';
    if (preInstalledSw) {
        if(preInstalledSw === PREINSTALLED_SOFTWARE.na) {
            formattedPreInstalledSw = constants.NA;
        } else if (preInstalledSw === PREINSTALLED_SOFTWARE.sqlEnt) {
            formattedPreInstalledSw = constants.SQL_SERVER_ENT;
        } else if (preInstalledSw === PREINSTALLED_SOFTWARE.sqlStd) {
            formattedPreInstalledSw = constants.SQL_SERVER_STD;
        } else if (preInstalledSw === PREINSTALLED_SOFTWARE.sqlWeb) {
            formattedPreInstalledSw = constants.SQL_SERVER_WEB;
        }
    }
    return formattedPreInstalledSw;
}
